/**
 * Dashboard greeting / "Updated HH:MM" timestamp module.
 *
 * Lightweight helper extracted from the inline ``<script>`` that used
 * to live at the bottom of ``templates/office/dashboard/dashboard.html``
 * (Phase 21.D migration). Its single job is to refresh the
 * ``#dash-updated-time`` element to the current local time every time
 * one of the dashboard's HTMX widgets finishes swapping — gives the
 * user a passive "the dashboard is alive" cue without forcing them to
 * manually refresh.
 *
 * Design notes
 * ------------
 *
 * * **Module-level singleton listener.** The dashboard contains 8+
 *   independent HTMX poll widgets (stats, next-up, team, finances,
 *   weekly, quotes, activity, map, compliance, staffing). Each fires
 *   ``htmx:afterSwap`` on its own swap target every 30-300 s. A naive
 *   per-init listener would multiply on each ``data-auto-init`` re-run
 *   (boost back/forward, soft-reload). We bind ONCE per SPA lifetime
 *   on ``document`` and guard with ``_dashGreetingBound``.
 *
 * * **Defensive DOM lookup inside the handler.** The
 *   ``#dash-updated-time`` element only exists on the dashboard page;
 *   the listener no-ops everywhere else. This is cheaper than rebinding
 *   the listener on each navigation and matches the pattern used by
 *   ``utils/declarative-attrs.js``.
 *
 * * **Local time, zero-padded HH:MM.** Matches the server-rendered
 *   format from the Django template (``{{ now|time:"H:i" }}``) so the
 *   user does not see a format jump between page-load and first poll.
 *
 * Auto-init contract: the dashboard wrapper carries
 * ``data-auto-init="dashboardGreeting"``; ``app.js`` then calls
 * ``App.pages.autoInitDashboardGreeting()`` on first load and on every
 * ``htmx:afterSwap`` of ``#main-content`` (boosted nav back to the
 * dashboard).
 */

let _bound = false;

function refreshTimestamp() {
  const el = document.getElementById('dash-updated-time');
  if (!el) return;
  const now = new Date();
  const hh = now.getHours().toString().padStart(2, '0');
  const mm = now.getMinutes().toString().padStart(2, '0');
  el.textContent = hh + ':' + mm;
}

export function initDashboardGreeting() {
  if (_bound) return;
  _bound = true;
  document.addEventListener('htmx:afterSwap', refreshTimestamp);
}

export function autoInitDashboardGreeting() {
  initDashboardGreeting();
}
